import { CheckCircleTwoTone } from "@ant-design/icons";
import { ExpandableConfig } from "antd/es/table/interface";
import to from "await-to-js";
import { t } from "i18next";
import cloneDeep from "lodash.clonedeep";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "../../../../components/Button/Button";
import { Input } from "../../../../components/Input/Input";
import { Table } from "../../../../components/Table/Table";
import { ModalsContext } from "../../../../context/modals.context";
import { TrackAndTraceContext } from "../../../../modules/trackAndTrace/trackAndTrace.context";
import { trackAndTraceService } from "../../../../modules/trackAndTrace/trackAndTrace.service";
import {
  ITtIstorijaKodaPayload,
  Tracking,
} from "../../../../modules/trackAndTrace/trackAndTrace.types";
import { useScreenResize } from "../../../../utils/useScreenResize";
import { TrackInfoModal } from "../components/TrackInfoModal/TrackInfoModal";
import style from "./IstorijaPracenja.module.scss";

interface IIstorijaPracenjaProps {
  type?: string;
}

const INITIAL_PARAMS = () => ({
  pib: null,
  kod: null,
});

export const IstorijaPracenja: React.FC<IIstorijaPracenjaProps> = ({
  type,
}) => {
  const { trackAndTrace, setTrackAndTrace } = useContext(TrackAndTraceContext);
  const { setError, handleUpdateModals, modals } = useContext(ModalsContext);
  const [params, setParams] = useState(INITIAL_PARAMS());
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const { width } = useScreenResize();

  const updateParams = (
    accessor: keyof ITtIstorijaKodaPayload,
    value: string | null | number
  ) => {
    setParams({ ...params, [accessor]: value || null });
    postTtIstorijaKoda();
  };

  const tableExpandConfig: ExpandableConfig<Tracking> = {
    expandedRowKeys,
    expandedRowClassName: () => style.expandedRow,
    showExpandColumn: false,
    onExpand: (expandable, record) => {
      if (expandable) {
        // @ts-ignore
        setExpandedRowKeys([...expandedRowKeys, record.customId as number]);
      } else {
        setExpandedRowKeys(
          // @ts-ignore
          expandedRowKeys.filter((id) => record.customId !== id)
        );
      }
    },
  };

  //type is ministry

  const postTtIstorijaKoda = useDebouncedCallback(async () => {
    handleUpdateModals({ isLoading: true });
    const [err, res] = await to(
      trackAndTraceService.postTtIstorijaKoda(params)
    );
    if (err || !res.success) return setError(res?.errorMessage);
    handleUpdateModals({ isLoading: false });
    setTrackAndTrace({
      pracenjeTabela: res.data.map((ttCode) => ({
        ...ttCode,
        customId: ttCode.id! + Math.random() * 38235315,
      })),
    });
  }, 400);

  const postTtIstorijaKodaSastav = async (
    id: number,
    tipPakovanjaId: number
  ) => {
    handleUpdateModals({ isLoading: true });
    const [err, res] = await to(
      trackAndTraceService.postTtIstorijaKodaSastav({ id, tipPakovanjaId })
    );
    handleUpdateModals({ isLoading: false });
    if (err || !res.success) return setError(res?.errorMessage);
    const newPracenje = cloneDeep(trackAndTrace.pracenjeTabela);
    const parentCode = newPracenje.find((item) => item.id === id);

    if (!parentCode) return;
    // @ts-ignore
    parentCode.children = res.data.map((ttItem) => ({
      ...ttItem,
      nested: true,
      customId: (ttItem.id ?? 0) + Math.random() * 31274123,
    }));
    setTrackAndTrace({
      pracenjeTabela: newPracenje,
    });
  };

  const clearSastav = (id: number) => {
    const newPracenje = cloneDeep(trackAndTrace.pracenjeTabela);
    const parentCode = newPracenje.find((item) => item.id === id);
    if (!parentCode) return;
    // @ts-ignore
    parentCode.children = undefined;
    setTrackAndTrace({
      pracenjeTabela: newPracenje,
    });
  };

  const renderKodCell = (kod: string) => (
    <div onClick={() => updateParams("kod", kod)} className={style.codeCell}>
      {kod}
    </div>
  );

  const colDefMfin = [
    {
      title: t("TRACK.TIP_PAKOVANJA"),
      dataIndex: "tipPakovanja",
      fixed: true,
      width: "120px",
    },
    {
      title: t("TRACK.IDENTIFIKACIONI_KOD"),
      dataIndex: "idKod",
      width: "130px",
      fixed: true,
      render: renderKodCell,
    },
    {
      title: t("PU_OBRASCI.PIB"),
      dataIndex: "pib",
      width: "120px",
      hideColumn: type !== "mfin",
    },
    {
      title: t("TRACK.NAZIV_DRUSTVA"),
      dataIndex: "nazivDrustva",
      width: "120px",
      hideColumn: type !== "mfin",
    },
    {
      title: t("TRACK.OBJEKAT"),
      dataIndex: "objekat",
      width: "120px",
    },
    {
      title: t("TRACK.DRUSTVO_SLANJE"),
      dataIndex: "drustvoSlanje",
      width: "120px",
    },
    {
      title: t("TRACK.OBJEKAT_SALJE_T"),
      dataIndex: "objekatSlanje",
      width: "120px",
    },

    {
      title: t("PLACEHOLDERS.STATUS"),
      dataIndex: "status",
      width: "120px",
    },
    {
      title: t("TRACK.UPAKOVANO_U"),
      dataIndex: "zbirnoPakovanje",
      width: "120px",
      render: renderKodCell,
    },
    {
      title: t("TRACK.DATUM_VREME"),
      dataIndex: "datum",
      width: "120px",
    },
    {
      title: t("TRACK.OTPREMNICA"),
      dataIndex: "otpremnica",
      width: "120px",
      render: (record: Tracking) =>
        record ? <CheckCircleTwoTone className={style.checkmark} /> : null,
    },
    {
      title: t("TRACK.SASTAV"),
      dataIndex: "sastav",
      width: "120px",
      render: (_: any, item: Tracking) =>
        !item.nested &&
        item.tipPakovanjaId !== 1 && (
          <Button
            className={style.button}
            onClick={() => {
              item.children
                ? clearSastav(item.id!)
                : postTtIstorijaKodaSastav(
                    item.id as number,
                    item.tipPakovanjaId as number
                  );
            }}
          >
            {item.children ? t("COMMON.ZATVORI") : t("TRACK.SASTAV")}
          </Button>
        ),
    },
    {
      title: t("TRACK.DETALJI"),
      dataIndex: "datum",
      width: "120px",
      render: (_: any, item: Tracking) =>
        !item.nested ? (
          <Button
            className={style.button}
            onClick={() =>
              handleUpdateModals({
                modalVisible: "trackInfo",
                pretovar: item.pretovar,
                vozilo: item.voziloSlanje,
                napomena: item.napomena,
                ttId: item.id,
                tipPakovanjaId: item.tipPakovanjaId,
                infoTitle: t("TRACK.DETALJI"),
              })
            }
          >
            {t("TRACK.DETALJI")}
          </Button>
        ) : (
          <></>
        ),
    },
  ];

  const colDefObveznik = colDefMfin.filter((col) => !col.hideColumn);
  const colDef = type ? colDefMfin : colDefObveznik;

  useEffect(() => {
    const ids = trackAndTrace.pracenjeTabela
      .filter((md: any) => md.children) //@ts-ignore
      .map((md) => md.customId);
    setExpandedRowKeys(ids as any);
  }, [trackAndTrace.pracenjeTabela]);

  useEffect(() => {
    return () => {
      setTrackAndTrace({ pracenjeTabela: [] });
    };
  }, []);

  return (
    <div className={style.mainContainer}>
      <TrackInfoModal />
      <div id="istorija">
        <h2>{t("TRACK.ISTORIJA_KODOVA")}</h2>
        <div className={style.buttonContainer}>
          <Input
            allowClear
            className="w-48"
            placeholder={t("TRACK.KOD")}
            onChange={(e) => updateParams("kod", e.target.value)}
            value={params.kod || ""}
            name="kod-track"
          ></Input>
          {type ? (
            <Input
              allowClear
              className="w-48"
              onChange={(e) => updateParams("pib", e.target.value)}
              placeholder={t("PU_OBRASCI.PIB")}
              value={params.pib || ""}
              name="pib-track"
            ></Input>
          ) : null}
        </div>
      </div>
      <Table
        loading={modals.isLoading}
        rowClassName={(record) =>
          record.children ? style.expandedParentRow : style.ttRow
        }
        expandable={tableExpandConfig}
        columns={colDef}
        scrollConfig={width > 1000 ? ["#istorija"] : undefined}
        rowKey="customId"
        dataSource={trackAndTrace.pracenjeTabela}
        name="istorija-table"
      />
    </div>
  );
};
